interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    has_more: boolean;
  };
  warnings?: string[];
}

interface AuthContext {
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
    role: 'viewer' | 'analyst' | 'admin';
    permissions: string[];
  };
  expires: number;
}

class ApiClient {
  private baseUrl: string;
  private auth: AuthContext | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, ''); // Remove trailing slash
    this.loadAuthFromStorage();
  }

  private loadAuthFromStorage(): void {
    try {
      const stored = localStorage.getItem('cylift_auth');
      if (stored) {
        const auth = JSON.parse(stored) as AuthContext;
        // Check if token is expired
        if (auth.expires * 1000 > Date.now()) {
          this.auth = auth;
        } else {
          localStorage.removeItem('cylift_auth');
        }
      }
    } catch (error) {
      console.warn('Failed to load auth from storage:', error);
      localStorage.removeItem('cylift_auth');
    }
  }

  private saveAuthToStorage(auth: AuthContext): void {
    try {
      localStorage.setItem('cylift_auth', JSON.stringify(auth));
    } catch (error) {
      console.warn('Failed to save auth to storage:', error);
    }
  }

  setAuth(auth: AuthContext): void {
    this.auth = auth;
    this.saveAuthToStorage(auth);
  }

  clearAuth(): void {
    this.auth = null;
    localStorage.removeItem('cylift_auth');
  }

  getAuth(): AuthContext | null {
    return this.auth;
  }

  isAuthenticated(): boolean {
    return this.auth !== null && this.auth.expires * 1000 > Date.now();
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.auth?.token) {
      headers.Authorization = `Bearer ${this.auth.token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.clearAuth();
          throw new Error('Authentication required');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ApiResponse<T> = await response.json();
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string | number>): Promise<ApiResponse<T>> {
    const url = new URL(endpoint, this.baseUrl);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
      });
    }
    return this.request<T>(url.pathname + url.search);
  }

  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  // Security Events API
  async getSecurityEvents(params?: {
    page?: number;
    limit?: number;
    severity?: string;
    event_type?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.get('/api/security/events', params);
  }

  async getSecurityEvent(id: string) {
    return this.get(`/api/security/events/${id}`);
  }

  async getSecurityStats(params?: {
    period?: string;
    start_time?: string;
    end_time?: string;
  }) {
    return this.get('/api/security/stats', params);
  }

  async getSecurityAlerts(params?: {
    page?: number;
    limit?: number;
    status?: string;
    severity?: string;
    channel?: string;
  }) {
    return this.get('/api/security/alerts', params);
  }

  async getSecurityAlertSummary() {
    return this.get('/api/security/alerts/summary');
  }

  async updateAlertStatus(id: string, status: string) {
    return this.put(`/api/security/alerts/${id}/status`, { status });
  }

  async getAlertRules() {
    return this.get('/api/security/rules');
  }

  async createAlertRule(rule: any) {
    return this.post('/api/security/rules', rule);
  }

  // Compliance API
  async getComplianceMetrics() {
    return this.get('/api/compliance/metrics');
  }

  async getComplianceProjects(params?: {
    page?: number;
    limit?: number;
    status?: string;
    framework?: string;
  }) {
    return this.get('/api/compliance/projects', params);
  }

  async getComplianceProject(id: string) {
    return this.get(`/api/compliance/projects/${id}`);
  }

  async createComplianceProject(project: any) {
    return this.post('/api/compliance/projects', project);
  }

  async getFrameworks(params?: {
    type?: string;
    sector?: string;
  }) {
    return this.get('/api/compliance/frameworks', params);
  }

  async getFrameworkControls(id: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    severity?: string;
    category?: string;
  }) {
    return this.get(`/api/compliance/frameworks/${id}/controls`, params);
  }

  async updateControlStatus(id: string, status: string, notes?: string) {
    return this.put(`/api/compliance/controls/${id}/status`, { status, notes });
  }

  async getControlEvidence(id: string) {
    return this.get(`/api/compliance/controls/${id}/evidence`);
  }

  async addControlEvidence(id: string, evidence: any) {
    return this.post(`/api/compliance/controls/${id}/evidence`, evidence);
  }

  async getRiskAcceptances(params?: {
    page?: number;
    limit?: number;
    status?: string;
    control_id?: string;
  }) {
    return this.get('/api/compliance/risk-acceptances', params);
  }

  async createRiskAcceptance(acceptance: any) {
    return this.post('/api/compliance/risk-acceptances', acceptance);
  }

  async getConformanceRules(params?: {
    framework?: string;
    enabled?: boolean;
  }) {
    const queryParams: Record<string, string | number> = {};
    if (params?.framework) queryParams.framework = params.framework;
    if (params?.enabled !== undefined) queryParams.enabled = params.enabled ? 'true' : 'false';
    return this.get('/api/compliance/conformance-rules', Object.keys(queryParams).length > 0 ? queryParams : undefined);
  }

  // Unified Dashboard API
  async getDashboardOverview(params?: {
    period?: string;
    start_time?: string;
    end_time?: string;
    project?: string;
    framework?: string;
    repo?: string;
  }) {
    const filteredParams = Object.fromEntries(
      Object.entries(params || {}).filter(([_, value]) => value !== undefined)
    );
    return this.get('/api/dashboard/overview', filteredParams);
  }

  async getSecurityTimeline(params?: {
    period?: string;
    granularity?: string;
    event_type?: string;
    severity?: string;
  }) {
    return this.get('/api/dashboard/security/timeline', params);
  }

  async getComplianceProgress(params?: {
    period?: string;
    framework?: string;
    project_id?: string;
  }) {
    return this.get('/api/dashboard/compliance/progress', params);
  }

  async getDashboardActivity(params?: {
    limit?: number;
    type?: string;
  }) {
    return this.get('/api/dashboard/activity', params);
  }

  async getDashboardInsights() {
    return this.get('/api/dashboard/insights');
  }

  // Remediations API
  async getRemediations(params?: {
    status?: string;
    priority?: string;
    category?: string;
    source?: string;
    limit?: number;
    offset?: number;
  }) {
    return this.get('/api/remediations', params);
  }

  async getRemediation(id: string) {
    return this.get(`/api/remediations/${id}`);
  }

  async createRemediation(remediation: any) {
    return this.post('/api/remediations', remediation);
  }

  async updateRemediationStatus(id: string, status: string, resolution?: string) {
    return this.put(`/api/remediations/${id}/status`, { status, resolution });
  }

  async getRemediationActions(id: string) {
    return this.get(`/api/remediations/${id}/actions`);
  }

  async recordRemediationAction(id: string, action: any) {
    return this.post(`/api/remediations/${id}/actions`, action);
  }

  async executeRemediationAction(id: string) {
    return this.post(`/api/remediations/${id}/execute`);
  }
}

// Create singleton instance - use relative URLs so it goes through the worker proxy
const apiClient = new ApiClient(
  import.meta.env.VITE_API_URL || ''
);

export default apiClient;